import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  LearnerFormEditor,
  toast,
} from "@eduviaos/ui-kit";
import type { LearnerFormUpdateRequest, LearnerFormVertical } from "@eduviaos/shared";
import { useTenantTeamForm, useUpdateTenantTeamForm } from "@/lib/teamForm/api";

const PERSON_NOUN: Record<LearnerFormVertical, string> = {
  study_centre: "team member",
  school: "staff member",
  coaching_institute: "staff member",
};

/** Phase SC-22 — super admin editor for a tenant's team invite form. */
export function TeamFormPanel({ tenantSlug }: { tenantSlug: string }) {
  const formQuery = useTenantTeamForm(tenantSlug);
  const save = useUpdateTenantTeamForm(tenantSlug);
  const form = formQuery.data;
  const noun = form ? PERSON_NOUN[form.vertical] : "team member";

  async function handleSave(body: LearnerFormUpdateRequest) {
    try {
      await save.mutateAsync(body);
      toast.success("Team form saved.");
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : "Couldn't save the form.");
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>Team form</CardTitle>
          {form?.is_customised ? <Badge variant="secondary">Customised</Badge> : null}
        </div>
        <CardDescription>
          The fields shown when this tenant invites a {noun}. Rename, reorder, hide or require
          standard fields, and add extra fields.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {formQuery.isLoading ? (
          <div className="grid gap-2" aria-busy="true">
            {Array.from({ length: 4 }, (_, i) => (
              <div
                key={i}
                className="bg-muted h-10 animate-pulse rounded-md motion-reduce:animate-none"
              />
            ))}
          </div>
        ) : formQuery.isError || !form ? (
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <p className="text-destructive" role="alert">
              Couldn&apos;t load the team form.
            </p>
            <Button variant="outline" size="sm" onClick={() => void formQuery.refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <LearnerFormEditor
            form={form}
            saving={save.isPending}
            onSave={(body) => void handleSave(body)}
            personNoun={noun}
          />
        )}
      </CardContent>
    </Card>
  );
}
